import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { AlertService } from '@digilize/shared/feature/modules/alert';
import { DialogService } from '@digilize/shared/utils/services/src';
import { APP_CONFIG, ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { ALERT_403, ALERT_429, ALERT_500, AppConfig, EnvConfig, PossibleApiPrefix, Token } from '@shared/definitions';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';

/* Straight at the file, not through this lib's own index: importing the barrel from inside it made
   the interceptor pull the login module — and `SharedUiComponentsModule` with the editor bundle
   behind it — into the initial bundle of every app that only registers this interceptor. */
import { AuthService } from './auth.service';

/*
Interceptor
- working for all /api/* requests
- handling  500, 429 errors
- proxy to API host
- token injection

For testing http errors use:
const dupReq = request.clone({ url: `http://httpstat.us/500` });
*/

export interface ApiInterceptorOptions {
  refreshTokenManualRetry?: boolean;
  loginNotRequiredUrlList?: string[];
  /**
   * Renew on a refusal as well as ahead of one: a 401 triggers a single renewal, the requests that
   * arrive while it runs queue behind it, and each is replayed once with the new token.
   *
   * Off by default so the twelve other apps registering this interceptor keep today's behaviour and can
   * adopt it deliberately (PAUL-3246). The proactive path is a clock and can therefore be wrong — after
   * a session is evicted server-side, or when the browser's clock disagrees — and this is the path that
   * recovers from that instead of stranding the user.
   */
  reactiveTokenRefresh?: boolean;
}

export const API_INTERCEPTOR_OPTIONS = new InjectionToken('API_INTERCEPTOR', {
  factory: (): ApiInterceptorOptions => ({
    refreshTokenManualRetry: false,
    loginNotRequiredUrlList: [],
    reactiveTokenRefresh: false,
  }),
});

/** Marks a request already replayed behind a renewed token, so a second refusal reaches the caller. */
const ALREADY_RETRIED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  /** The renewal in flight per backend, shared by every request that arrives while it runs. */
  private refreshInFlight: Partial<Record<PossibleApiPrefix, Observable<Token | null>>> = {};

  constructor(
    @Inject(ENV_CONFIG) private envConfig: EnvConfig,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    @Optional() @Inject(API_INTERCEPTOR_OPTIONS) private options: ApiInterceptorOptions,
    public authService: AuthService,
    private alertService: AlertService,
    private dialogService: DialogService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const apiType = this.getApiType(request.url);

    if (!!apiType) {
      const activeRouteRequiresNoLogin =
        this.options?.loginNotRequiredUrlList?.length &&
        this.options?.loginNotRequiredUrlList?.some((urlPart) => window.location.href.includes(urlPart));
      if (activeRouteRequiresNoLogin) {
        return this.setRequest(request, next);
      } else {
        return this.setRequest(request, next).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status >= 500 && error.status < 600) {
              this.alertService.setAlert({
                ...ALERT_500,
                requestId: error.error?.error?.request_id,
                apiError: error.error?.error,
              });
            } else if (error.status === 429) {
              this.alertService.setAlert(ALERT_429);
            } else if (error.status === 403) {
              if (
                !this.authService.isLoggedIn() &&
                !request.url.includes('password-change') && // Wrong password when changing password
                !request.url.includes('sessions') && // Claiming session
                !this.isAuthEndpoint(request.url) // Wrong password in login
              ) {
                this.alertService.setAlert(ALERT_403);
                if (!this.appConfig?.disabledAuthPopup) {
                  this.openSignInDialog();
                }
              }
            }
            return throwError(error);
          })
        );
      }
    }

    return next.handle(request);
  }

  /**
   * Fetched when a 403 actually asks for it. A static reference here anchors the login module — and
   * everything its `SharedUiComponentsModule` import brings — to the initial bundle of every app
   * that registers this interceptor, for a dialog most of them never open.
   */
  private openSignInDialog(): void {
    import('./sign-in-up/sign-in-up.component').then(({ SignInUpComponent }) =>
      this.dialogService.openDialog(SignInUpComponent)
    );
  }

  private setRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Set proxy for all requests
    const apiType = this.getApiType(request.url);
    request = this.setProxy(request, apiType);

    // Don't use interceptor for refresh and token
    if (this.isAuthEndpoint(request.url)) {
      return next.handle(request);
    }

    const activeRouteRequiresNoLogin =
      this.options?.loginNotRequiredUrlList?.length &&
      this.options.loginNotRequiredUrlList?.some((urlPart) => window.location.href.includes(urlPart));

    if (!activeRouteRequiresNoLogin) {
      const accessExpired = !this.authService.getAccessToken(apiType);
      const refreshExpired = !this.authService.getRefreshToken(apiType);

      // No tokens valid
      if (accessExpired && refreshExpired) {
        return next.handle(request);
      }

      // Access token expired - need to refresh token
      if (accessExpired && !refreshExpired) {
        return this.refreshOnce(apiType).pipe(switchMap(() => this.send(request, next, apiType)));
      }

      // Access token valid
      return this.send(request, next, apiType);
    } else {
      return next.handle(request);
    }
  }

  /**
   * Sends the request with the current token, and — where the app has opted in — renews once and replays
   * it if the server refuses it.
   *
   * A 401 is the server's own verdict, so this path is immune to the two things the proactive clock
   * cannot see: a browser whose clock disagrees with the API's, and a session evicted server-side
   * because the account hit its simultaneous-session cap. That eviction is what PAUL-3246 was, and
   * before this the only ways out were a hard reload or nothing at all.
   */
  private send(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    apiType: PossibleApiPrefix
  ): Observable<HttpEvent<unknown>> {
    if (!this.options?.reactiveTokenRefresh) {
      return next.handle(this.injectToken(request, apiType));
    }

    return next.handle(this.injectToken(request, apiType)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!this.isRenewable(error, request, apiType)) {
          return throwError(error);
        }

        return this.refreshOnce(apiType).pipe(
          /* A refused renewal is an answer, not a second failure to report: AuthService has already
             routed to login. The caller still sees the original 401 rather than the renewal's error,
             which is the one it asked about. */
          catchError(() => of(null)),
          switchMap((renewed) =>
            renewed
              ? next.handle(
                  this.injectToken(request.clone({ context: request.context.set(ALREADY_RETRIED, true) }), apiType)
                )
              : throwError(error)
          )
        );
      })
    );
  }

  /** Strictly one replay per request, and only where there is still a session to renew with. */
  private isRenewable(error: HttpErrorResponse, request: HttpRequest<unknown>, apiType: PossibleApiPrefix): boolean {
    return (
      error?.status === 401 && !request.context.get(ALREADY_RETRIED) && !!this.authService.getRefreshToken(apiType)
    );
  }

  /**
   * The renewal for this backend, starting one only if none is running.
   *
   * Replaces a `refreshTokenInProgress` flag that was cleared only on the success path, so a single
   * refused renewal left it `true` for the lifetime of the page and every later request parked forever
   * on a subject that would never emit again — the "navigation silently dead" half of PAUL-3246. Here
   * the slot is released by `finalize`, on success and failure alike, and the queued requests are
   * completed with whatever the renewal actually did.
   */
  private refreshOnce(apiType: PossibleApiPrefix): Observable<Token | null> {
    const inFlight = this.refreshInFlight[apiType];

    if (inFlight) {
      return inFlight;
    }

    const refresh = this.refreshToken(apiType).pipe(
      /* Before shareReplay, so the slot frees when the call settles rather than when the last subscriber
         leaves — otherwise a later 401 would be handed this call's cached verdict. */
      finalize(() => {
        delete this.refreshInFlight[apiType];
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.refreshInFlight[apiType] = refresh;

    return refresh;
  }

  private refreshToken(apiType: PossibleApiPrefix): Observable<Token> {
    if (this.options?.refreshTokenManualRetry) {
      return this.authService.refreshAccessTokenManualRetry(apiType);
    }
    return this.authService.refreshAccessToken(apiType);
  }

  private injectToken(request: HttpRequest<unknown>, apiType: PossibleApiPrefix): HttpRequest<unknown> {
    const token = this.authService.getAccessToken(apiType);
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private setProxy(request: HttpRequest<unknown>, apiType: PossibleApiPrefix): HttpRequest<unknown> {
    return request.clone({
      // url: 'https://httpstat.us/403' // Use for testing error codes
      url:
        this.envConfig.production || this.envConfig.staging
          ? `${this.envConfig[apiType]}${request.url.replace(`/${apiType}`, '')}`
          : request.url,
    });
  }

  /**
   * Login, refresh and password reset live under an `auth` path segment (`/v1.0/auth/...`, `/client/auth`,
   * the external `/auth/refresh`). Matched as a segment, not a substring: `/v1.0/oauth/grants` (PAUL-3659)
   * is an ordinary signed-in call and was going out without its token.
   */
  private isAuthEndpoint(url: string): boolean {
    return url.split(/[?#]/)[0].split('/').includes('auth');
  }

  private getApiType(url: string): PossibleApiPrefix | undefined {
    if (url.split('/').some((urlPart) => urlPart === 'api')) {
      return 'api';
    }

    if (url.split('/').some((urlPart) => urlPart === 'api2')) {
      return 'api2';
    }

    return undefined;
  }
}
